import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose } from 'lodash';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import Field from 'redux-form/lib/Field';
import reduxForm from 'redux-form/lib/reduxForm';
import formValueSelector from 'redux-form/lib/formValueSelector';
import Link from '../../../components/Link';
import history from '../../../core/history';
import BodyEditor from '../emailTemplates/BodyEditor';
import EmailPreview from './EmailPreview';
import validate from './validate';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import { getEmailTemplates, getEmailLogo, updateEmailTemplate } from '../../../lib/graphql';
import { url } from '../../../config';
import Loader from '../../../components/Loader/Loader';
import messages from '../../../locale/jsonMessages';
import CommonFormComponent from '../../../components/CommonField/CommonFormComponent';
import ToolTipSection from '../../../components/Common/ToolTipSection/ToolTipSection';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import { GoBackArrow } from '../../../components/Common/Assets/SvgIconComponent/CommonSvgIcon';
import bt from '../../../components/commonStyle.css';


const EMAIL_TEMPLATE_LABEL_KEYS = {
  documentVerification: 'emailTemplateTypeDocumentVerification',
  contact: 'emailTemplateTypeContact',
  contactSupport: 'emailTemplateTypeContactSupport',
  userFeedback: 'emailTemplateTypeUserFeedback',
  listPublishRequest: 'emailTemplateTypeListPublishRequest',
  adminListApprove: 'emailTemplateTypeAdminListApprove',
  adminListReject: 'emailTemplateTypeAdminListReject',
  servicePlanExpired: 'emailTemplateTypeServicePlanExpired',
  reportUser: 'emailTemplateTypeReportUser',
  welcomeEmail: 'emailTemplateTypeWelcomeEmail',
  confirmEmail: 'emailTemplateTypeConfirmEmail',
  bookingRequest: 'emailTemplateTypeBookingRequest',
  bookingRequestGuest: 'emailTemplateTypeBookingRequestGuest',
  bookingConfirmedToHost: 'emailTemplateTypeBookingConfirmedToHost',
  bookingConfirmedToGuest: 'emailTemplateTypeBookingConfirmedToGuest',
  bookingDeclinedToGuest: 'emailTemplateTypeBookingDeclinedToGuest',
  bookingExpiredGuest: 'emailTemplateTypeBookingExpiredGuest',
  bookingExpiredHost: 'emailTemplateTypeBookingExpiredHost',
  cancelledByHost: 'emailTemplateTypeCancelledByHost',
  cancelledByGuest: 'emailTemplateTypeCancelledByGuest',
  completedGuest: 'emailTemplateTypeCompletedGuest',
  completedHost: 'emailTemplateTypeCompletedHost',
  forgotPasswordLink: 'emailTemplateTypeForgotPasswordLink',
  message: 'emailTemplateTypeMessage',
  inquiry: 'emailTemplateTypeInquiry',
  bookingPreApproval: 'emailTemplateTypeBookingPreApproval',
  banStatusServiceStatusBanned: 'emailTemplateTypeBanStatusServiceStatusBanned',
  banStatusServiceStatusUnBanned: 'emailTemplateTypeBanStatusServiceStatusUnBanned',
  documentVerification: 'emailTemplateTypeDocumentVerification'
};

function EmailTemplateBodyField({ input, type, isClient, intl, onQuillReady, meta: { touched, error } }) {
  if (isClient) {
    return (
      <div>
        <BodyEditor
          type={type}
          value={input.value || ''}
          onChange={input.onChange}
          onQuillReady={onQuillReady}
        />
        {touched && error && <span className='errorMessageText'>{intl.formatMessage(error)}</span>}
      </div>
    );
  }

  return (
    <textarea
      {...input}
      placeholder={intl.formatMessage(messages.emailTemplateLoadingEditor)}
      readOnly
    />
  );
}

function EmailTemplateEdit(props) {
  const {
    type,
    data: { getEmailTemplates: dbTemplates = [], loading, refetch },
    emailLogoData,
    mutateUpdateEmailTemplate,
    handleSubmit,
    initialize,
    subjectValue,
    previewTextValue,
    bodyHtmlValue
  } = props;
  const logoValue = emailLogoData?.getEmailLogo?.value;
  const logoUrl = logoValue ? `${url}/images/logo/${logoValue}` : '';
  const siteName = useSelector(state => state.siteSettings.data.siteName);
  const intl = useIntl();
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    setIsClient(typeof window !== 'undefined');
  }, []);

  useEffect(() => {
    if (loading || initialized) return;
    const row = (dbTemplates || []).find((t) => t.type === type) || {};
    initialize({
      subject: row.subject || '',
      previewText: row.previewText || '',
      bodyHtml: row.bodyHtml || '',
    });
    setInitialized(true);
  }, [dbTemplates, loading, type, initialized, initialize]);

  useEffect(() => {
    setInitialized(false);
  }, [type]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const updateEmailTemplateData = await mutateUpdateEmailTemplate({
        variables: {
          type,
          subject: values?.subject || null,
          previewText: values?.previewText || null,
          bodyHtml: values?.bodyHtml || null,
        },
      });
      await refetch();
      if (updateEmailTemplateData?.data?.updateEmailTemplate?.status == 200) {
        return await showToaster({ messageId: "emailTemplateUpdateSuccess", toasterType: "success" })
      } else {
        return await showToaster({ messageId: "emailTemplateUpdateFailed", toasterType: "error" })
      }
    } catch (err) {
      console.error(err);
      return await showToaster({ messageId: "emailTemplateUpdateFailed", toasterType: "error" })
    } finally {
      setSaving(false);
    }
  };

  if (loading && !initialized) {
    return <Loader type="text" />;
  }

  return (
    <div>
      <div className={cx(bt.dFlexWrap, bt.justifyContentSpaceBetween, bt.gap8, bt.spaceBottom4)}>
        <h1 className={cx('siteAdminHeading', bt.noMarginBottom)}>  {intl.formatMessage(messages.emailTemplateEditTitle, {
          label: EMAIL_TEMPLATE_LABEL_KEYS[type] ? intl.formatMessage(messages[EMAIL_TEMPLATE_LABEL_KEYS[type]]) : type,
        })}</h1>
        <Link to="/siteadmin/email-templates" className='goBackLink'>
          <GoBackArrow />
          {intl.formatMessage(messages.goBack)}
        </Link>
      </div>
      <form onSubmit={handleSubmit(handleSave)}>
        <div className='twoColumnGrid'>
          <Field
            name="subject"
            component={CommonFormComponent}
            placeholder={intl.formatMessage(messages.emailTemplateTableSubject)}
            inputClass='commonInputField'
            labelText={intl.formatMessage(messages.emailTemplateTableSubject)}
            toolTipText={intl.formatMessage(messages.emailSubjectToolTip)}
          />
          <Field
            name="previewText"
            component={CommonFormComponent}
            placeholder={intl.formatMessage(messages.emailTemplatePreviewTextLabel)}
            inputClass='commonInputField'
            labelText={intl.formatMessage(messages.emailTemplatePreviewTextLabel)}
            toolTipText={intl.formatMessage(messages.emailPreviewTextToolTip)}
          />
        </div>

        <div className={bt.spaceTop3}>
          <label className={'commonLabelText'}>{intl.formatMessage(messages.emailTemplateBodyLabel)}<ToolTipSection label={intl.formatMessage(messages.emailBodyToolTip)} /></label>
          <Field
            name="bodyHtml"
            component={EmailTemplateBodyField}
            type={type}
            isClient={isClient}
            intl={intl}
            onQuillReady={(q) => { quillRef.current = q; }}
          />
          <div className={bt.commonNoteText}>
            {intl.formatMessage(messages.emailTemplatePlaceholderHint)}
          </div>
        </div>
        <div className={cx(bt.dividerBorder, bt.spaceTop3, bt.spaceBottom3)} />
        {isClient ? (
          <div>
            <label className={cx('commonLabelText', bt.commonContentText, bt.spaceBottom4, bt.fontWeightBold)}>{intl.formatMessage(messages.emailPreviewSectionLabel)}<ToolTipSection label={intl.formatMessage(messages.emailPreviewSectionToolTip)} /></label>
            <EmailPreview
              subject={subjectValue}
              previewText={previewTextValue}
              bodyHtml={bodyHtmlValue}
              type={type}
              logoUrl={logoUrl}
              siteName={siteName}
            />
          </div>
        ) : null}
        <div className={cx('singleBtnFlexEnd', bt.spaceTop3)} >
          <Loader
            type='button'
            label={intl.formatMessage(messages.save)}
            handleClick={handleSubmit(handleSave)}
            show={saving}
            buttonType='submit'
            className={cx(bt.btnPrimary, 'loaderButtonFlex')}
          />
        </div>
      </form>
    </div>
  );
}

EmailTemplateEdit.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  data: PropTypes.shape({
    getEmailTemplates: PropTypes.array,
    loading: PropTypes.bool,
    refetch: PropTypes.func,
  }),
  emailLogoData: PropTypes.shape({
    getEmailLogo: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  siteNameData: PropTypes.shape({
    getSiteName: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  mutateUpdateEmailTemplate: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  subjectValue: PropTypes.string,
  previewTextValue: PropTypes.string,
  bodyHtmlValue: PropTypes.string,
};

EmailTemplateEdit.defaultProps = {
  title: '',
  data: {
    getEmailTemplates: [],
    loading: true,
    refetch: () => { },
  },
  emailLogoData: {},
  siteNameData: {},
  subjectValue: '',
  previewTextValue: '',
  bodyHtmlValue: '',
};

EmailTemplateEdit = reduxForm({
  form: 'EmailTemplateEditForm',
  enableReinitialize: false,
  validate
})(EmailTemplateEdit);

const selector = formValueSelector('EmailTemplateEditForm');

const mapState = (state) => ({
  subjectValue: selector(state, 'subject') || '',
  previewTextValue: selector(state, 'previewText') || '',
  bodyHtmlValue: selector(state, 'bodyHtml') || '',
});

export default compose(
  connect(mapState),
  withStyles(bt),
  withQuery(getEmailTemplates, {
    options: () => ({ fetchPolicy: 'network-only', ssr: false }),
  }),
  withQuery(getEmailLogo, {
    name: 'emailLogoData',
    options: () => ({ fetchPolicy: 'network-only', ssr: false }),
  }),
  withMutation(updateEmailTemplate, { name: 'mutateUpdateEmailTemplate' }),
)(EmailTemplateEdit);
